import ConfirmationModal from "@/components/ui/confirmationModal";
import { useAppSelector } from "@/redux/hooks/hooks";
import { useState } from "react";
import toast from "react-hot-toast";
import MobileProductSkeleton from "./MobileProductSkeleton";
import DesktopProductSkeleton from "./DesktopProductSkeleton";
import TableMobileView from "./TableMobileView";
import TableDesktopView from "./TableDesktopView";
import { useDeleteProductMutation } from "@/redux/feature/videoDashboard/VideoDashboardApi";
import { Product } from "@/redux/feature/videoDashboard/videoDashboardType";

// Combined Product Row Skeleton
const ProductRowSkeleton = ({ index }: { index: number }) => (
  <div key={`skeleton-${index}`}>
    <MobileProductSkeleton />
    <DesktopProductSkeleton />
  </div>
);

const ProductsTable = ({
  products,
  isLoading,
  error,
}: {
  products: Product[];
  isLoading: boolean;
  error?: string | undefined;
}) => {
  const [selectedBrand, setSelectedBrand] = useState<Product | null>(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [deleteProduct, { isLoading: isDeleting }] = useDeleteProductMutation();

  const { user } = useAppSelector((state) => state.auth);

  // Handler to delete
  const handleDelete = async (product: Product) => {
    try {
      await deleteProduct({
        email: user?.email as string,
        brand: product.brandName,
        product: product.productName,
        country: product.country,
      }).unwrap();
      setIsConfirmModalOpen(false);
      toast.success("Deleted successfully!");
    } catch (err) {
      toast.error("Failed to delete");
    }
  };
  return (
    <div className="w-full">
      {/* Desktop Header */}
      <div className="hidden md:block">
        <div className="bg-gradient-to-r from-slate-50 to-slate-100/50 border border-slate-200 rounded-t-xl">
          <div className="grid grid-cols-12 px-8 py-5 gap-6">
            <div className="col-span-3">
              <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
                Product Name
              </h3>
            </div>

            <div className="col-span-1 text-center">
              <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
                Status
              </h3>
            </div>
            <div className="col-span-2 text-center">
              <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
                Brand Videos
              </h3>
            </div>
            <div className="col-span-2 text-center">
              <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
                Competitor Videos
              </h3>
            </div>
            <div className="col-span-2 text-center">
              <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
                Last Updated
              </h3>
            </div>
            <div className="col-span-2 text-right">
              <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
                Actions
              </h3>
            </div>
          </div>
        </div>
      </div>

      {/* Content Area */}
      {isLoading ? (
        // Loading State - Show skeletons
        <div className="space-y-1 md:space-y-0">
          {Array.from({ length: 5 }).map((_, index) => (
            <ProductRowSkeleton key={`skeleton-${index}`} index={index} />
          ))}
        </div>
      ) : error ? (
        // Error State
        <div className="bg-white border border-red-200 rounded-xl md:rounded-t-none">
          <div className="py-20 text-center px-6">
            <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-6">
              <div className="text-red-500 text-2xl">⚠️</div>
            </div>
            <h3 className="text-xl font-semibold text-red-900 mb-3">
              Error loading products
            </h3>
            <p className="text-red-600 max-w-md mx-auto leading-relaxed">
              {error}
            </p>
          </div>
        </div>
      ) : products && products.length > 0 ? (
        // Products List
        <div className="md:bg-white md:border md:border-slate-200 md:border-t-0 md:rounded-b-xl md:overflow-hidden">
          <div className="space-y-1 md:space-y-0 md:divide-y md:divide-slate-100">
            {products.map((brand, index) => (
              <div
                key={`product-${index}`}
                className="group transition-all duration-200"
              >
                {/* Mobile view - card layout */}
                <TableMobileView brand={brand} />

                {/* Desktop view - table layout */}
                <TableDesktopView
                  brand={brand}
                  isDeleting={isDeleting}
                  setSelectedBrand={setSelectedBrand}
                  setIsConfirmModalOpen={setIsConfirmModalOpen}
                />
              </div>
            ))}
          </div>
        </div>
      ) : (
        // Empty State
        <div className="bg-white border border-slate-200 rounded-xl md:rounded-t-none">
          <div className="py-20 text-center px-6">
            <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <div className="text-4xl">📦</div>
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mb-3">
              No products found
            </h3>
            <p className="text-slate-600 max-w-md mx-auto leading-relaxed">
              Try adjusting your search or filters to find what you're looking
              for.
            </p>
          </div>
        </div>
      )}

      <ConfirmationModal
        title="Are you sure you want to delete this product?"
        isOpen={isConfirmModalOpen}
        setOpen={setIsConfirmModalOpen}
        onConfirm={() => handleDelete(selectedBrand as Product)}
      />
    </div>
  );
};

export default ProductsTable;
